/**
 * Playback Controller – Steuert die Wiedergabe von TTS-Audio.
 * State Machine: idle → loading → playing → paused → idle
 */

import * as ttsService from './ttsService.js';
import * as audioCache from './audioCache.js';
import * as usageTracker from './usageTracker.js';
import * as settingsManager from './settingsManager.js';
import * as syncAudio from './sync/syncAudio.js';
import { splitTextAtSentenceBoundaries, MAX_TTS_BYTES } from './textSegmenter.js';

/**
 * @typedef {'idle'|'loading'|'playing'|'paused'} PlaybackState
 */

/** @type {PlaybackState} */
let state = 'idle';

/** @type {HTMLAudioElement|null} */
let audioElement = null;

/** @type {import('./textSegmenter.js').Segment[]} */
let segments = [];

/** Current segment index */
let currentIndex = -1;

/** Whether we're in continuous playback mode */
let continuousMode = false;

/** Temporary selection parts to play */
let selectionParts = [];
let currentSelectionIndex = -1;

/** AbortController for prefetching the next segment */
let prefetchAbortController = null;

/** AbortController for preloading all segments */
let preloadAbortController = null;

/** Event listeners */
const stateListeners = [];

/**
 * Initializes the playback controller
 */
export function init() {
  audioElement = new Audio();
  audioElement.addEventListener('ended', handleAudioEnded);
  audioElement.addEventListener('error', handleAudioError);
  audioElement.addEventListener('timeupdate', handleTimeUpdate);
  audioElement.addEventListener('durationchange', handleTimeUpdate);
  setVolume(settingsManager.get('volume') ?? 1);
  continuousMode = autoplayEnabled();
}

/**
 * Whether autoplay is switched on in the settings
 * @returns {boolean}
 */
function autoplayEnabled() {
  return settingsManager.get('autoplay') !== false;
}

/**
 * Returns the audio URL for a text: from this device's cache, else from the
 * sync server, else freshly synthesized. Only synthesis costs characters; a new
 * recording is cached, counted and shared via the sync server.
 * @param {string} text
 * @param {AbortSignal} [signal]
 * @returns {Promise<string>}
 */
async function loadAudioUrl(text, signal) {
  const settings = settingsManager.getAll();
  const { voiceName, languageCode, speakingRate, pitch, audioEncoding } = settings;

  const cached = await audioCache.get(text, settings);
  if (cached) return cached.audioUrl;

  const cacheKey = await audioCache.generateCacheKey(text, settings);
  const mimeType = ttsService.getMimeType(audioEncoding);
  const synced = await syncAudio.download(cacheKey, mimeType, signal);
  if (synced) {
    const audioUrl = URL.createObjectURL(synced);
    await audioCache.set(text, settings, synced, audioUrl);
    return audioUrl;
  }

  const result = await ttsService.synthesize(text, {
    apiKey: settings.apiKey,
    languageCode,
    voiceName,
    speakingRate,
    pitch,
    audioEncoding,
    signal,
  });

  await audioCache.set(text, settings, result.audioBlob, result.audioUrl);
  usageTracker.addChars(result.charCount, ttsService.extractTier(voiceName));
  syncAudio.upload(cacheKey, result.audioBlob);
  return result.audioUrl;
}

/**
 * Loads an audio URL and starts playing it
 * @param {string} text
 */
async function loadAndPlay(text) {
  const audioUrl = await loadAudioUrl(text);
  if (!audioElement) return;
  audioElement.src = audioUrl;
  await audioElement.play();
  setState('playing');
}

/**
 * Sets the playback volume (persistent across segments)
 * @param {number} volume - Volume between 0 and 1
 */
export function setVolume(volume) {
  const v = Math.min(1, Math.max(0, Number(volume) || 0));
  if (audioElement) {
    audioElement.volume = v;
  }
}

/**
 * Returns the current playback volume
 * @returns {number} Volume between 0 and 1
 */
export function getVolume() {
  return audioElement ? audioElement.volume : 1;
}

function handleTimeUpdate() {
  if (!audioElement) return;
  const currentTime = audioElement.currentTime || 0;
  const duration = audioElement.duration || 0;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  emitTimeUpdate({ currentTime, duration, progress });
}

/**
 * Sets the segments to play
 * @param {import('./textSegmenter.js').Segment[]} newSegments
 */
export function setSegments(newSegments) {
  stop();
  segments = newSegments;
  currentIndex = -1;
}

/**
 * Sets the current segment index without playing
 * @param {number} index
 */
export function setCurrentIndex(index) {
  if (index < 0 || index >= segments.length) return;
  currentIndex = index;
  emitSegmentChange(index);
}

/**
 * Starts playing from the beginning or current position
 * @param {boolean} [continuous] - Play continuously through all segments.
 *   Defaults to the autoplay setting.
 */
export async function play(continuous = autoplayEnabled()) {
  continuousMode = continuous;

  if (state === 'paused' && audioElement) {
    audioElement.play();
    setState('playing');
    return;
  }

  if (segments.length === 0) return;

  const startIndex = currentIndex >= 0 ? currentIndex : 0;
  await playSegment(startIndex);
}

/**
 * Plays a specific segment by index
 * @param {number} index
 */
export async function playSegment(index) {
  if (index < 0 || index >= segments.length) return;

  const segment = segments[index];
  currentIndex = index;

  setState('loading');
  emitSegmentChange(index);

  try {
    await loadAndPlay(segment.plainText);
    prefetchNextSegment();
  } catch (error) {
    setState('idle');
    emitError(error.message);
  }
}

/**
 * Prefetches the next segment's audio in the background and caches it
 */
async function prefetchNextSegment() {
  if (!continuousMode) return;

  const nextIndex = currentIndex + 1;
  if (nextIndex < 0 || nextIndex >= segments.length) return;

  const nextSegment = segments[nextIndex];

  try {
    if (await audioCache.has(nextSegment.plainText, settingsManager.getAll())) return;

    // Abort any existing prefetch
    if (prefetchAbortController) {
      prefetchAbortController.abort();
    }
    prefetchAbortController = new AbortController();

    await loadAudioUrl(nextSegment.plainText, prefetchAbortController.signal);
    emitPrefetchComplete(nextIndex);
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Prefetch aborted for segment index:', nextIndex);
    } else {
      console.error('Prefetch error:', error);
    }
  } finally {
    prefetchAbortController = null;
  }
}

/**
 * Lädt die Audiodaten aller Abschnitte nacheinander in den Cache, damit der
 * Text anschließend ohne Wartezeiten und offline vorgelesen werden kann.
 * @param {(done: number, total: number) => void} [onProgress]
 * @returns {Promise<{done: number, total: number, cancelled: boolean}>}
 */
export async function preloadAll(onProgress) {
  const total = segments.length;
  if (total === 0 || preloadAbortController) {
    return { done: 0, total, cancelled: false };
  }

  preloadAbortController = new AbortController();
  const { signal } = preloadAbortController;
  let done = 0;

  try {
    await checkSyncedAudio();
    for (const segment of segments) {
      if (signal.aborted) break;
      await loadAudioUrl(segment.plainText, signal);
      done++;
      onProgress?.(done, total);
    }
  } catch (error) {
    if (error.name !== 'AbortError') throw error;
  } finally {
    preloadAbortController = null;
    syncAudio.forgetAvailability();
  }

  return { done, total, cancelled: done < total };
}

/**
 * Fragt beim Sync-Server mit einer Anfrage ab, welche Abschnitte er schon hat –
 * so versucht das Vorladen nur dort einzelne Abrufe
 */
async function checkSyncedAudio() {
  const settings = settingsManager.getAll();
  const keys = await Promise.all(
    segments.map((segment) => audioCache.generateCacheKey(segment.plainText, settings))
  );
  await syncAudio.checkAvailability(keys);
}

/**
 * Stops a running preload
 */
export function cancelPreload() {
  preloadAbortController?.abort();
}

/**
 * Whether segments are currently being preloaded
 * @returns {boolean}
 */
export function isPreloading() {
  return preloadAbortController !== null;
}

/**
 * Plays only the selected/highlighted text
 * @param {string} selectedText
 */
export async function playSelection(selectedText) {
  if (!selectedText?.trim()) return;

  // Eine Auswahl wird immer am Stück gelesen – Autoplay greift erst wieder
  // beim nächsten regulären Abschnitt (siehe endSelection).
  continuousMode = false;

  // Split selected text by bytes
  const parts = splitTextAtSentenceBoundaries(selectedText, MAX_TTS_BYTES);

  if (parts.length > 1) {
    selectionParts = parts;
    await playSelectionPart(0);
  } else {
    selectionParts = [];
    currentSelectionIndex = -1;
    await playSelectionSingle(selectedText);
  }
}

/**
 * Plays a single part of selected text
 * @param {string} text
 */
async function playSelectionSingle(text) {
  setState('loading');
  try {
    await loadAndPlay(text);
  } catch (error) {
    setState('idle');
    emitError(error.message);
  }
}

/**
 * Plays a specific part from selectionParts by index
 * @param {number} index
 */
async function playSelectionPart(index) {
  if (index < 0 || index >= selectionParts.length) {
    endSelection();
    return;
  }

  currentSelectionIndex = index;
  setState('loading');

  try {
    await loadAndPlay(selectionParts[index]);
  } catch (error) {
    endSelection();
    emitError(error.message);
  }
}

/**
 * Ends selection playback and restores the configured autoplay mode
 */
function endSelection() {
  selectionParts = [];
  currentSelectionIndex = -1;
  continuousMode = autoplayEnabled();
  setState('idle');
}

/**
 * Pauses playback
 */
export function pause() {
  if (state === 'playing' && audioElement) {
    audioElement.pause();
    setState('paused');
  }
}

/**
 * Stops playback and resets position
 */
export function stop() {
  if (audioElement) {
    audioElement.pause();
    // removeAttribute + load statt src='' – ein leerer src-String löst
    // einen error-Event aus (Fehler-Toast bei jedem Stop)
    audioElement.removeAttribute('src');
    audioElement.load();
  }
  if (prefetchAbortController) {
    prefetchAbortController.abort();
    prefetchAbortController = null;
  }
  cancelPreload();
  currentIndex = -1;
  continuousMode = autoplayEnabled();
  selectionParts = [];
  currentSelectionIndex = -1;
  setState('idle');
  emitSegmentChange(-1);
  emitTimeUpdate({ currentTime: 0, duration: 0, progress: 0 });
}

/**
 * Goes to the next segment
 */
export async function next() {
  if (currentIndex < segments.length - 1) {
    await playSegment(currentIndex + 1);
  }
}

/**
 * Goes to the previous segment
 */
export async function prev() {
  if (currentIndex > 0) {
    await playSegment(currentIndex - 1);
  }
}

export async function togglePlayPause() {
  if (state === 'playing') {
    pause();
  } else if (state === 'paused') {
    await play(continuousMode);
  } else {
    await play();
  }
}

/**
 * Toggles autoplay (continuous) mode
 */
export async function toggleAutoplay() {
  setAutoplay(!continuousMode);

  // Einschalten im Ruhezustand startet die Wiedergabe direkt
  if (continuousMode && state === 'idle') {
    await play(true);
    return;
  }

  if (continuousMode && state === 'playing') {
    prefetchNextSegment();
  }
  // Ohne Zustandswechsel – meldet den Listenern nur den neuen Autoplay-Modus
  setState(state);
}

/**
 * Switches autoplay on or off and remembers the choice in the settings
 * @param {boolean} enabled
 */
function setAutoplay(enabled) {
  continuousMode = enabled;
  settingsManager.save({ autoplay: enabled });

  if (!enabled && prefetchAbortController) {
    prefetchAbortController.abort();
    prefetchAbortController = null;
  }
}

/**
 * Checks if continuous playback is active
 * @returns {boolean}
 */
export function isContinuous() {
  return continuousMode;
}

/**
 * Handler when audio finishes playing
 */
function handleAudioEnded() {
  if (selectionParts.length > 0 && currentSelectionIndex >= 0) {
    if (currentSelectionIndex < selectionParts.length - 1) {
      playSelectionPart(currentSelectionIndex + 1);
    } else {
      endSelection();
    }
  } else if (continuousMode && currentIndex < segments.length - 1) {
    // Play next segment
    playSegment(currentIndex + 1);
  } else {
    setState('idle');
    if (!continuousMode || currentIndex >= segments.length - 1) {
      emitSegmentChange(-1);
    }
  }
}

/**
 * Handler for audio errors
 * @param {Event} event
 */
function handleAudioError(_event) {
  setState('idle');
  emitError('Audio-Wiedergabefehler.');
}

/**
 * Gets the current state
 * @returns {PlaybackState}
 */
export function getState() {
  return state;
}

/**
 * Gets the current segment index
 * @returns {number}
 */
export function getCurrentIndex() {
  return currentIndex;
}

/**
 * Sets the state and notifies listeners
 * @param {PlaybackState} newState
 */
function setState(newState) {
  state = newState;
  if (newState === 'idle') {
    if (prefetchAbortController) {
      prefetchAbortController.abort();
      prefetchAbortController = null;
    }
  }
  for (const listener of stateListeners) {
    listener({ state: newState, segmentIndex: currentIndex });
  }
}

/**
 * Registers a state change listener
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export function onStateChange(callback) {
  stateListeners.push(callback);
  return () => {
    const idx = stateListeners.indexOf(callback);
    if (idx >= 0) stateListeners.splice(idx, 1);
  };
}

/** Segment change listeners */
const segmentChangeListeners = [];

/**
 * Registers a segment change listener
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export function onSegmentChange(callback) {
  segmentChangeListeners.push(callback);
  return () => {
    const idx = segmentChangeListeners.indexOf(callback);
    if (idx >= 0) segmentChangeListeners.splice(idx, 1);
  };
}

function emitSegmentChange(index) {
  for (const listener of segmentChangeListeners) {
    listener(index);
  }
}

/** Prefetch complete listeners */
const prefetchCompleteListeners = [];

/**
 * Registers a prefetch complete listener
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export function onPrefetchComplete(callback) {
  prefetchCompleteListeners.push(callback);
  return () => {
    const idx = prefetchCompleteListeners.indexOf(callback);
    if (idx >= 0) prefetchCompleteListeners.splice(idx, 1);
  };
}

function emitPrefetchComplete(index) {
  for (const listener of prefetchCompleteListeners) {
    listener(index);
  }
}

/** Error listeners */
const errorListeners = [];

/**
 * Registers an error listener
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export function onError(callback) {
  errorListeners.push(callback);
  return () => {
    const idx = errorListeners.indexOf(callback);
    if (idx >= 0) errorListeners.splice(idx, 1);
  };
}

function emitError(message) {
  for (const listener of errorListeners) {
    listener(message);
  }
}

/**
 * Seeks to a specific percentage (0 to 100)
 * @param {number} percentage
 */
export function seek(percentage) {
  if (audioElement && audioElement.duration) {
    const time = (percentage / 100) * audioElement.duration;
    audioElement.currentTime = time;
  }
}

/** Time update listeners */
const timeUpdateListeners = [];

/**
 * Registers a time update listener
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export function onTimeUpdate(callback) {
  timeUpdateListeners.push(callback);
  return () => {
    const idx = timeUpdateListeners.indexOf(callback);
    if (idx >= 0) timeUpdateListeners.splice(idx, 1);
  };
}

function emitTimeUpdate(data) {
  for (const listener of timeUpdateListeners) {
    listener(data);
  }
}
